import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  DateTimeDisplay,
  ModalError,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { globalStyles } from "styles";
import { getErrors, textCapitalization } from "utils";
import { hardBaseUrl } from "services/urlConstant";

function AnnouncementManagement() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams.get("search");
  const paramsPage = urlParams.get("page");
  const paramsSize = urlParams.get("per_page");
  const paramsType = urlParams.get("type");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    per_page: Number(paramsSize) || 10,
    search: paramsSearch || "",
    type: paramsType || "email",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleChangePage = (_, page) => handleChangeQueryParams(page, "page");
  const handleChangePageSize = e =>
    handleChangeQueryParams(e?.target?.value, "per_page");
  const handleSearch = e => handleChangeQueryParams(e?.target?.value, "search");
  const handleAdd = () =>
    history.push(
      `/admin/konfigurasi/announcement-management/add/${queryParams?.type}`
    );
  const handleView = item =>
    history.push(
      `/admin/konfigurasi/announcement-management/view/${item?.id}/${queryParams?.type}`
    );
  const handleEdit = item =>
    history.push(
      `/admin/konfigurasi/announcement-management/edit/${item?.id}/${queryParams?.type}`
    );
  const handleRestore = () => {};

  const getDataTable = async () => {
    const { page, per_page, search, type } = queryParams;
    const params = { page, per_page, search };
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/admin/annoucement/table/${type}`,
        { headers, params }
      );

      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.per_page);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Announcement Management">
      <Container maxWidth={false}>
        <HeaderTitle title="Configuration" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          my="16px"
        >
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={handleSearch}
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <SelectInput
                  options={optionsType}
                  optionKey="type"
                  optionLabel="title"
                  value={queryParams?.type || ""}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "type")
                  }
                  width="100%"
                  disabled={loadingPage}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label={`Create ${
                    queryParams?.type === "web"
                      ? "System Broadcast Content"
                      : "Email Announcement"
                  }`}
                  onClick={handleAdd}
                  startIcon={<Add />}
                  size="large"
                  disabled={loadingPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable({ type: queryParams?.type })}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.per_page}
          pageCount={tableTotalPage}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
          isHaveAction
          handleView={handleView}
          disabledEdit={item => item?.status === "published"}
          handleEdit={handleEdit}
          disabledRestore={item => item?.status !== "draft"}
          handleRestore={handleRestore}
        />
      </Container>
    </Page>
  );
}
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Configuration",
    link: "/admin/konfigurasi/announcement-management",
  },
  {
    label: "Announcement Management",
    active: true,
  },
];
const optionsType = [
  {
    type: "web",
    title: "System Broadcast Content",
  },
  {
    type: "email",
    title: "Email Announcement",
  },
];
const columnTable = ({ type }) => [
  ...(type === "web"
    ? [
        {
          name: "id",
          title: "ID",
        },
      ]
    : []),
  {
    name: "subject",
    title: type === "web" ? "Title" : "Subject",
  },
  {
    name: type === "web" ? "start_date" : "delivery_time",
    title: type === "web" ? "Start Date" : "Delivery Time",
    renderText: date => <DateTimeDisplay date={date} />,
  },
  ...(type === "web"
    ? [
        {
          name: "end_date",
          title: "End Date",
          renderText: date => <DateTimeDisplay date={date} />,
        },
      ]
    : []),
  ...(type === "email"
    ? [
        {
          name: "recipient",
          title: "Recipient",
          renderText: item => textCapitalization(item || ""),
        },
      ]
    : []),
  {
    name: "status",
    title: "Status",
    renderText: status => {
      const newStatus = textCapitalization(status || "");
      const typeChip = {
        published: "success",
        scheduled: "warning",
        draft: "warning",
        expired: "danger",
      };
      return <StatusChip type={typeChip[status]} label={newStatus} />;
    },
  },
];

export default AnnouncementManagement;
